import * as React from 'react'

import { Trash as TrashIcon } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { EmbroideryService } from '../../../containers/EmbroideryService'
import { intlPrettyCurrency } from '../../../lib/utils'
import { EmbroiderySectionWrapper } from '../EmbroiderySectionWrapper'
import {
  DTCLogoSneakPeek,
  Props as DtcLogoSneakPeekProps,
} from '../EmbroiderySneakPeek/DTCLogoSneakpeek'
import { IconSneakPeek, Props as IconSneakPeekProps } from '../EmbroiderySneakPeek/IconSneakPeek'
import { LogoSneakPeek, Props as LogoSneakPeekProps } from '../EmbroiderySneakPeek/LogoSneakPeek'
import { SneakPeeksWrapper } from '../EmbroiderySneakPeek/styles'
import { TextSneakPeek, Props as TextSneakPeekProps } from '../EmbroiderySneakPeek/TextSneakPeek'

import {
  ActionsBar,
  Body,
  EditButton,
  EmbroideryCheckmark,
  PortalDiscountText,
  RightButtons,
  ToggleAddEmbroideryButton,
  ToggleAddEmbroideryCheckbox,
  ToggleAddEmbroideryText,
  TrashButton,
} from './styles'

interface Props {
  clearSelectedEmbroideryFormOptions(): void
  confirmAddEmbroidery: boolean
  editEmbroideryText: string
  embroideryOptionPrice: number | undefined
  openEmbroideryModal(): void
  toggleAddEmbroideryText: string
  toggleConfirmAddEmbroidery(): void
  textSneakPeekProps: TextSneakPeekProps
  iconSneakPeekProps: IconSneakPeekProps
  logoSneakPeekProps: LogoSneakPeekProps
  dtcLogoSneakPeekProps: DtcLogoSneakPeekProps
  embroideryDiscountMessage?: string
  canEditEmbroidery?: boolean
}

export const Template: React.FC<Props> = ({
  clearSelectedEmbroideryFormOptions,
  confirmAddEmbroidery,
  editEmbroideryText,
  embroideryOptionPrice,
  openEmbroideryModal,
  toggleAddEmbroideryText,
  toggleConfirmAddEmbroidery,
  textSneakPeekProps,
  iconSneakPeekProps,
  logoSneakPeekProps,
  dtcLogoSneakPeekProps,
  embroideryDiscountMessage,
  canEditEmbroidery,
}) => {
  const addEmbroideryLabelId: string = 'add-embroidery-label'
  const { locale, region } = useLocalization()
  const { editEmbroideryRef } = EmbroideryService.useContainer()

  return (
    <EmbroiderySectionWrapper className='embroidery-settings-section' hasBorder>
      <Body>
        <ActionsBar>
          <ToggleAddEmbroideryButton
            aria-label='Add Embroidery'
            onClick={toggleConfirmAddEmbroidery}
            disabled={!canEditEmbroidery}
          >
            <ToggleAddEmbroideryCheckbox
              aria-checked={confirmAddEmbroidery}
              aria-labelledby={addEmbroideryLabelId}
              role='checkbox'
              tabIndex={0}
              isDisabled={!canEditEmbroidery}
            >
              {confirmAddEmbroidery && <EmbroideryCheckmark isDisabled={!canEditEmbroidery} />}
            </ToggleAddEmbroideryCheckbox>

            <ToggleAddEmbroideryText id={addEmbroideryLabelId}>
              {toggleAddEmbroideryText}
            </ToggleAddEmbroideryText>
          </ToggleAddEmbroideryButton>

          <RightButtons>
            {embroideryOptionPrice !== undefined && (
              <span className='money'>
                +
                {intlPrettyCurrency({
                  cents: embroideryOptionPrice,
                  region: region,
                  locale: locale,
                })}
              </span>
            )}

            <EditButton
              onClick={openEmbroideryModal}
              isOnlyButton={!canEditEmbroidery}
              aria-label='Edit Embroidery'
              ref={editEmbroideryRef}
            >
              {editEmbroideryText}
            </EditButton>

            {canEditEmbroidery && (
              <TrashButton
                onClick={clearSelectedEmbroideryFormOptions}
                aria-label='Remove Embroidery'
              >
                <TrashIcon />
              </TrashButton>
            )}
          </RightButtons>
        </ActionsBar>
        {!!embroideryDiscountMessage && (
          <PortalDiscountText>{embroideryDiscountMessage}.</PortalDiscountText>
        )}
        <SneakPeeksWrapper>
          <TextSneakPeek {...textSneakPeekProps} />
          <IconSneakPeek {...iconSneakPeekProps} />
          <LogoSneakPeek {...logoSneakPeekProps} />
          <DTCLogoSneakPeek {...dtcLogoSneakPeekProps} />
        </SneakPeeksWrapper>
      </Body>
    </EmbroiderySectionWrapper>
  )
}
